import { parseCSV } from "./parse-csv";
import { validateFieldValue } from "./validate-field-value";

/**
 * Built-in field types supported by CSV validation
 */
export enum CSVFieldType {
  STRING = "string",
  NUMBER = "number",
  INTEGER = "integer",
  TIMESTAMP = "timestamp",
  BOOLEAN = "boolean",
  EMAIL = "email",
  URL = "url",
  PHONE = "phone",
  POSTAL_CODE = "postal_code",
}

/**
 * Type alias for CSV field type values
 */
export type CSVFieldTypeValue = `${CSVFieldType}`;

/**
 * CSV validation error with row and field context
 */
export interface CSVValidationError {
  row: number; // Row number (1-indexed, 0 = header)
  field: string; // Field name
  message: string; // Error/warning message
  severity: "error" | "warning"; // Severity level
}

/**
 * Custom validator function type
 */
export type CustomValidator<T = unknown> = (
  value: string,
  row: Record<string, unknown>,
  rowIndex: number
) => { value: T; error?: CSVValidationError } | null;

/**
 * Field schema configuration
 */
export interface FieldSchema<T = unknown> {
  type: CSVFieldTypeValue | CustomValidator;
  required?: boolean;
  unique?: boolean;
  // Type-specific constraints
  minLength?: number; // string, email, url
  maxLength?: number; // string, email, url
  min?: number; // number, integer
  max?: number; // number, integer
  pattern?: RegExp; // string, email, phone
  allowedValues?: unknown[]; // enum constraint for any type
  // Custom validation
  validate?: (value: unknown, row: T, rowIndex: number) => CSVValidationError | null;
}

/**
 * CSV validation configuration
 */
export interface CSVValidationConfig<T = Record<string, unknown>> {
  schema: {
    [fieldName: string]: FieldSchema<T>;
  };
  options?: {
    minRows?: number;
    maxRows?: number;
    allowUnknownColumns?: boolean; // Default: true (permissive)
    continueOnError?: boolean; // Default: true (skip invalid rows, continue processing)
  };
}

/**
 * CSV validation result
 */
export interface CSVValidationResult<T = Record<string, unknown>> {
  data: T[]; // Successfully validated rows
  errors: CSVValidationError[]; // Critical errors (blocked rows)
  warnings: CSVValidationError[]; // Non-critical issues (row still processed)
  isValid: boolean; // True if no errors (warnings don't affect this)
  metadata: {
    totalRows: number;
    validRows: number;
    invalidRows: number;
    headers: string[];
  };
}

/**
 * Helper to set a value in a nested object using dot notation
 * Example: setNestedValue({}, "a.b.c", 5) => { a: { b: { c: 5 } } }
 */
const setNestedValue = (obj: Record<string, unknown>, path: string, value: unknown): void => {
  const keys = path.split(".");
  let current: Record<string, unknown> = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (key === undefined) continue;
    if (!(key in current)) {
      current[key] = {};
    } else if (typeof current[key] !== "object" || current[key] === null) {
      // If there's a conflicting non-object value, overwrite it
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  const lastKey = keys[keys.length - 1];
  if (lastKey !== undefined) {
    current[lastKey] = value;
  }
};

/**
 * Validate and parse CSV content
 * @param input - CSV string or pre-parsed 2D array of strings
 * @param config - Validation configuration with schema and options
 * @returns Validation result with data, errors, warnings, and metadata
 *
 * @example
 * const config = {
 *   schema: {
 *     name: { type: CSVFieldType.STRING, required: true },
 *     "address.city": { type: CSVFieldType.STRING },
 *   }
 * } as const;
 *
 * const result = validateCSV(csvContent, config);
 * // result.data is automatically typed as { name: string; address?: { city?: string } }[]
 */
export const validateCSV = <TConfig extends CSVValidationConfig>(
  input: string | string[][],
  config: TConfig
): CSVValidationResult<InferCSVType<TConfig>> => {
  const errors: CSVValidationError[] = [];
  const warnings: CSVValidationError[] = [];
  const options = {
    allowUnknownColumns: true,
    continueOnError: true,
    ...config.options,
  };

  // Parse CSV if input is a string
  const rows = typeof input === "string" ? parseCSV(input) : input;

  // Check for empty CSV
  if (rows.length === 0) {
    return {
      data: [],
      errors: [{ row: 0, field: "", message: "CSV file is empty", severity: "error" }],
      warnings: [],
      isValid: false,
      metadata: {
        totalRows: 0,
        validRows: 0,
        invalidRows: 0,
        headers: [],
      },
    };
  }

  // Extract and validate headers
  // rows.length === 0 is already handled above; this access is safe
  const firstRow = rows[0];
  if (!firstRow) {
    return {
      data: [],
      errors: [{ row: 0, field: "", message: "CSV file is empty", severity: "error" }],
      warnings: [],
      isValid: false,
      metadata: { totalRows: 0, validRows: 0, invalidRows: 0, headers: [] },
    };
  }
  const headers = firstRow.map((h) => h.trim());
  const dataRows = rows.slice(1);

  // Check row limits
  if (options.minRows !== undefined && dataRows.length < options.minRows) {
    errors.push({
      row: 0,
      field: "",
      message: `CSV must have at least ${options.minRows} rows (got ${dataRows.length})`,
      severity: "error",
    });
  }

  if (options.maxRows !== undefined && dataRows.length > options.maxRows) {
    errors.push({
      row: 0,
      field: "",
      message: `CSV must have at most ${options.maxRows} rows (got ${dataRows.length})`,
      severity: "error",
    });
  }

  // Validate headers
  const schemaFields = Object.keys(config.schema);
  const requiredFields = schemaFields.filter((field) => config.schema[field]?.required);

  // Check for required fields
  const missingRequired = requiredFields.filter((field) => !headers.includes(field));
  if (missingRequired.length > 0) {
    errors.push({
      row: 0,
      field: missingRequired.join(", "),
      message: `Missing required columns: ${missingRequired.join(", ")}`,
      severity: "error",
    });
  }

  // Check for unknown columns if strict mode
  if (!options.allowUnknownColumns) {
    const unknownHeaders = headers.filter((h) => h && !schemaFields.includes(h));
    if (unknownHeaders.length > 0) {
      errors.push({
        row: 0,
        field: unknownHeaders.join(", "),
        message: `Unknown columns: ${unknownHeaders.join(", ")}. Only these fields are allowed: ${schemaFields.join(
          ", "
        )}`,
        severity: "error",
      });
    }
  }

  // If header validation failed, return early
  if (errors.length > 0) {
    return {
      data: [],
      errors,
      warnings,
      isValid: false,
      metadata: {
        totalRows: dataRows.length,
        validRows: 0,
        invalidRows: dataRows.length,
        headers,
      },
    };
  }

  // Parse and validate each row
  const parsedData: InferCSVType<TConfig>[] = [];
  const uniqueValueSets: Map<string, Set<unknown>> = new Map();

  // Initialize unique value sets
  schemaFields.forEach((field) => {
    if (config.schema[field]?.unique) {
      uniqueValueSets.set(field, new Set());
    }
  });

  dataRows.forEach((row, rowIndex) => {
    const rowNumber = rowIndex + 2; // +2 because: 0-indexed + header row
    const rowData: Record<string, unknown> = {};
    let hasError = false;

    headers.forEach((header, colIndex) => {
      if (!header) return;

      const value = row[colIndex]?.trim() || "";
      const schema = config.schema[header];

      // Skip unknown columns
      if (!schema) return;

      // Check required fields
      if (schema.required && !value) {
        errors.push({
          row: rowNumber,
          field: header,
          message: `Required field "${header}" is missing`,
          severity: "error",
        });
        hasError = true;
        return;
      }

      // Skip empty optional fields
      if (!value) return;

      // Validate and convert based on type
      const validationResult = validateFieldValue(value, header, schema, rowNumber);

      if (validationResult?.error) {
        if (validationResult.error.severity === "error") {
          errors.push(validationResult.error);
          hasError = true;
        } else {
          warnings.push(validationResult.error);
        }
      }

      // Store value if validation passed
      if (validationResult && validationResult.value !== null) {
        // Use dot notation to set nested values
        setNestedValue(rowData, header, validationResult.value);

        // Check unique constraint
        if (schema.unique) {
          const uniqueSet = uniqueValueSets.get(header);
          if (uniqueSet && uniqueSet.has(validationResult.value)) {
            errors.push({
              row: rowNumber,
              field: header,
              message: `Duplicate value for unique field "${header}": ${validationResult.value}`,
              severity: "error",
            });
            hasError = true;
          } else {
            uniqueSet?.add(validationResult.value);
          }
        }

        // Run custom validation if provided
        if (schema.validate) {
          const customError = schema.validate(validationResult.value, rowData as InferCSVType<TConfig>, rowIndex);
          if (customError) {
            if (customError.severity === "error") {
              errors.push(customError);
              hasError = true;
            } else {
              warnings.push(customError);
            }
          }
        }
      }
    });

    // Only add row if it doesn't have critical errors (or if continueOnError is false)
    if (!hasError || !options.continueOnError) {
      parsedData.push(rowData as InferCSVType<TConfig>);
    }
  });

  return {
    data: parsedData,
    errors,
    warnings,
    isValid: errors.length === 0,
    metadata: {
      totalRows: dataRows.length,
      validRows: parsedData.length,
      invalidRows: dataRows.length - parsedData.length,
      headers,
    },
  };
};

/**
 * Type inference utilities for converting CSV schema to TypeScript types
 */

/**
 * Maps CSV field types to their corresponding TypeScript types
 */
type CSVFieldTypeMap = {
  [CSVFieldType.STRING]: string;
  [CSVFieldType.NUMBER]: number;
  [CSVFieldType.INTEGER]: number;
  [CSVFieldType.TIMESTAMP]: number;
  [CSVFieldType.BOOLEAN]: boolean;
  [CSVFieldType.EMAIL]: string;
  [CSVFieldType.URL]: string;
  [CSVFieldType.PHONE]: string;
  [CSVFieldType.POSTAL_CODE]: string;
};

/**
 * Infers the TypeScript type from a field schema
 */
type InferFieldType<TSchema extends FieldSchema> = TSchema["type"] extends keyof CSVFieldTypeMap
  ? CSVFieldTypeMap[TSchema["type"]]
  : TSchema["type"] extends CustomValidator<infer R>
  ? R
  : unknown;

/**
 * Converts a dot-notation key to a nested object type
 * Example: "a.b.c" with type number => { a: { b: { c: number } } }
 */
type DotNotationToNested<K extends string, V> = K extends `${infer First}.${infer Rest}`
  ? { [P in First]: DotNotationToNested<Rest, V> }
  : { [P in K]: V };

/**
 * Helper type to convert union to intersection
 */
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never;

/**
 * Converts a CSV schema to the corresponding TypeScript type
 * Handles both flat keys and dot notation for nested objects
 */
type SchemaToType<TSchema extends Record<string, FieldSchema>> = {
  [K in keyof TSchema]: DotNotationToNested<
    K & string,
    TSchema[K]["required"] extends true ? InferFieldType<TSchema[K]> : InferFieldType<TSchema[K]> | undefined
  >;
}[keyof TSchema] extends infer U
  ? U extends Record<string, any>
    ? UnionToIntersection<U>
    : never
  : never;

/**
 * Infer the result type from a CSV validation config
 * Use this to get type-safe results from validateCSV
 *
 * @example
 * const config = {
 *   schema: {
 *     name: { type: CSVFieldType.STRING, required: true },
 *     age: { type: CSVFieldType.INTEGER },
 *     "address.city": { type: CSVFieldType.STRING },
 *     "address.zip": { type: CSVFieldType.POSTAL_CODE }
 *   }
 * } as const;
 *
 * type MyData = InferCSVType<typeof config>;
 * // Result: { name: string; age?: number; address?: { city?: string; zip?: string } }
 */
export type InferCSVType<TConfig extends CSVValidationConfig> = TConfig["schema"] extends Record<string, FieldSchema>
  ? SchemaToType<TConfig["schema"]>
  : never;
